import React, { useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { ReceiptText } from 'lucide-react';
import { SportsContext } from '../../_context/SportsContext';
import { formatOdds, formatDelta } from './_helpers/oddsFormat';
import { formatSportsMatchTimeKst } from './_helpers/sportsMatchTime';
import { sortSelectionsByDisplayOrder } from './_helpers/marketRowSorting';
import { buildMarketDisplayTitle } from './figma/marketTitleResolver';
import getCountryFlagUrl from './figma/countryFlagResolver';
import getSportIconUrl from './figma/sportIconResolver';
import getSelectionLabel from './figma/selectionLabelResolver';
import { SportsBetCartContext } from '../../_context/SportsBetCartContext';
import useServiceContextHook from '../../_hooks/service.context.hook';
import { dedupeMergedSelections } from './_helpers/marketDuplicateHelpers';
import { shouldRunInplaySilentRecovery } from './_helpers/inplayDisplayOrderingGuard';
import {
  enrichInplaySelectionMarketIdentity,
  getCustomerMarketKey,
  getInplayLineGroupStableKey,
  getInplaySelectionStableKey,
  getSourceMarketId,
} from '../../_helpers/inplayMarketIdentity';
import { getLiveHeaderText, getLivePhaseLabel, getLiveScore } from './_helpers/liveDisplay';
import {
  buildStickyLiveCardMatchIds,
  isTerminalLiveMatchStatus,
  resolveStickyLiveSelectedShell,
  resolveStickyLiveDetailTargetId,
  shouldClearLiveCardMarketCache,
} from './_helpers/inplayStickySubscription';
import './sports.css';
import './sports-live-desktop.css';
import TeamLogo from './TeamLogo';
import { SportsDetailSkeleton, SportsListSkeleton } from '../../components/Skeleton/Skeleton';

/** Selection sort 순서 — exact base_line 으로 묶인 후에도 카드 순서 일관성 보장. */
const _SEL_ORDER = {
  '1': 0, 'X': 1, 'x': 1, '2': 2,
  '1X': 0, 'X2': 1, '12': 2,
  'Over': 0, 'over': 0, 'Under': 1, 'under': 1,
  'Odd': 0, 'odd': 0, 'Even': 1, 'even': 1,
  'Home': 0, 'home': 0, 'Draw': 1, 'draw': 1, 'Away': 2, 'away': 2,
};
const _sortMergedOdds = (odds = []) => {
  const source = Array.isArray(odds) ? odds : [];
  const displayOrdered = sortSelectionsByDisplayOrder(source);
  if (source.some((odd) => odd?.displaySlot != null || odd?.displayOrder != null || odd?.display_slot != null || odd?.display_order != null)) {
    return displayOrdered;
  }
  return [...source].sort((a, b) => {
    const ao = _SEL_ORDER[String(a.name || '').trim()] ?? 99;
    const bo = _SEL_ORDER[String(b.name || '').trim()] ?? 99;
    return ao - bo;
  });
};

const isNotFoundSportsResponse = (response = {}) => {
  if (!response || response.networkError) return false;
  if (response.status !== false) return false;
  const marker = `${response.code || ''} ${response.message || ''} ${response.error || ''}`;
  return /NOT_FOUND|404|찾을 수 없습니다|경기를 찾을 수 없습니다/i.test(marker);
};

/** HC 계열 (Asian / European / Spread) 감지 — 사용자 확정 규칙 적용 대상. */
const _isHCMarket = (mkt) => {
  const code = String(mkt.market_code || mkt.market_type?.code || '');
  if (/^FEED_\d+_(3|13|53|61|64|65|66|67|95|250|281|342|407|433|935|1083|1248|1558|1693|1694|2286|2732)$/.test(code)) return true;
  const name = String(buildMarketDisplayTitle(mkt) || mkt.market_name || mkt.market_type_name || mkt.market_type?.name || mkt.name || '');
  return /핸디캡|handicap|spread/i.test(name);
};

const getStableMarketId = (mkt) => (
  getCustomerMarketKey(mkt)
);

const getStableMarketTypeId = (mkt) => (
  mkt?.market_type_id
  ?? mkt?.market_type?.id
  ?? mkt?.market_type?.code
  ?? mkt?.market_code
  ?? mkt?.contract_market_id
  ?? buildMarketDisplayTitle(mkt)
  ?? mkt?.market_name
  ?? ''
);

const getHandicapDisplayGroupTypeKey = (mkt) => (
  mkt?.market_type_id
  ?? mkt?.market_type?.id
  ?? mkt?.market_type?.code
  ?? mkt?.feed_market_id
  ?? mkt?.market_code
  ?? buildMarketDisplayTitle(mkt)
  ?? mkt?.market_name
  ?? ''
);

/** 사용자 확정 규칙 (2026-04-08): HC 계열만 exact base_line set.
 *  - HC: 같은 (market_type_id, exact base_line) 의 split row 들을 한 section 으로 합침
 *  - 비-HC: 기존대로 row 단위 단독 section (display_line/base_line/line fallback 표시)
 *  - 각 odds 에 source market_id 보존 (cart 의 market_id 정확성)
 */
const _groupMarketsByBaseLine = (markets) => {
  if (!markets) return [];
  const groups = new Map();
  for (const mk of markets) {
    const customerMarketKey = getCustomerMarketKey(mk);
    const sourceMarketId = getSourceMarketId(null, mk) ?? customerMarketKey;
    // initial REST suspend 도 inject — marketLatest 가 비어있는 첫 렌더에 fallback
    const sourceMarketSuspended = mk.is_suspended ?? false;
    const enrichedOdds = getMarketSelections(mk).map(o => ({
      ...enrichInplaySelectionMarketIdentity(o, mk),
      _sourceMarketSuspended: sourceMarketSuspended,
    }));
    if (_isHCMarket(mk)) {
      const mtId = getHandicapDisplayGroupTypeKey(mk);
      const baseLine = (mk.base_line ?? '').toString().trim();
      const key = baseLine
        ? `${mtId}::hc::${baseLine}`
        : `${mtId}::hcrow::${customerMarketKey || sourceMarketId || getMktTitle(mk)}`;
      if (!groups.has(key)) {
        groups.set(key, {
          ...mk,
          _groupKey: key,
          _customerMarketKey: customerMarketKey,
          _sourceMarketId: sourceMarketId,
          _isHCSet: true,
          _baseLineLabel: baseLine || null,
          _mergedOdds: enrichedOdds,
        });
      } else {
        groups.get(key)._mergedOdds.push(...enrichedOdds);
      }
    } else {
      const key = `nohc::${customerMarketKey || sourceMarketId || getStableMarketTypeId(mk) || getMktTitle(mk)}`;
      groups.set(key, {
        ...mk,
        _groupKey: key,
        _customerMarketKey: customerMarketKey,
        _sourceMarketId: sourceMarketId,
        _isHCSet: false,
        _baseLineLabel: null,
        _mergedOdds: enrichedOdds,
      });
    }
  }
  return Array.from(groups.values());
};

/** 종목 아이콘 매핑 */
const SPORT_ICONS = {
  football: '⚽', soccer: '⚽', '축구': '⚽',
  basketball: '🏀', '농구': '🏀',
  baseball: '⚾', '야구': '⚾',
  tennis: '🎾', '테니스': '🎾',
  volleyball: '🏐', '배구': '🏐',
  hockey: '🏒', '하키': '🏒',
  esports: '🎮', '이스포츠': '🎮',
  mma: '🥊', boxing: '🥊',
  default: '🏆'
};
const getSportIcon = (sport) => {
  if (!sport) return SPORT_ICONS.default;
  if (sport.icon_url && sport.icon_url.length <= 4) return sport.icon_url;
  const key = (sport.slug || sport.name || '').toLowerCase();
  return SPORT_ICONS[key] || SPORT_ICONS[(sport.name_ko || '')] || SPORT_ICONS.default;
};

/** 마켓 코드 분류 */
const MAIN_CODES = ['MATCH_RESULT', 'WINNER', 'MONEY_LINE', 'HANDICAP', 'SPREAD', 'OVER_UNDER', 'TOTALS', 'RUN_LINE'];
const HALF_KEYWORDS = ['HALF', '1H', '2H', 'FIRST_HALF', 'SECOND_HALF'];

const classifyMarket = (mkt) => {
  const code = (mkt.market_code || mkt.market_type?.code || '').toUpperCase();
  if (MAIN_CODES.some(mc => code.includes(mc))) return 'main';
  if (HALF_KEYWORDS.some(hk => code.includes(hk))) return 'half';
  return 'extra';
};

/** 마켓 탭 정의 */
const MARKET_TABS = [
  { key: 'main', label: '메인마켓' },
  { key: 'half', label: '전반전/후반전' },
  { key: 'extra', label: '추가배팅옵션' },
];

const MKT_NAME_MAP = {
  'MATCH_RESULT': '승무패', '1X2': '승무패', 'MONEY_LINE': '승패',
  'HANDICAP': '핸디캡', 'ASIAN_HANDICAP': '핸디캡', 'Asian Handicap': '핸디캡',
  'SPREAD': '스프레드', 'OVER_UNDER': '오버언더', 'TOTALS': '토탈',
  'BTTS': '양팀득점', 'Double Chance': '더블찬스', 'DOUBLE_CHANCE': '더블찬스',
  'ODD_EVEN': '홀짝',
};

const getMktTitle = (mkt) => {
  const resolvedTitle = buildMarketDisplayTitle(mkt);
  if (resolvedTitle) return resolvedTitle;
  const mt = mkt.market_type || {};
  const name = mkt.market_name || mkt.market_type_name || mt.name || mkt.market_code || '';
  if (MKT_NAME_MAP[name]) return MKT_NAME_MAP[name];
  if (mkt.market_code && MKT_NAME_MAP[mkt.market_code]) return MKT_NAME_MAP[mkt.market_code];
  return name || '마켓';
};

const getMktTitleWithLine = (mkt) => {
  const title = getMktTitle(mkt);
  const lineLabel = (mkt._isHCSet || _isHCMarket(mkt))
    ? (mkt._baseLineLabel ?? mkt.base_line ?? '').toString().trim()
    : String(mkt.display_line || mkt.base_line || mkt.line || '').trim();
  if (!lineLabel) return title;
  return `${title} (${lineLabel.replace(/\s*\(0-0\)\s*$/, '')})`;
};

const renderSportIcon = (sport) => {
  const url = getSportIconUrl(sport);
  return url
    ? <img src={url} alt="" className="zi_sport_icon" />
    : <span className="zi_sport_icon_text">{getSportIcon(sport)}</span>;
};

/** 시간 포맷 — fixed Asia/Seoul, never the browser's local timezone */
const formatTime = formatSportsMatchTimeKst;

const getLiveTimeDisplay = (match) => getLivePhaseLabel(match);
const getLiveHeaderTime = (match, now) => getLiveHeaderText(match, now) || (match?.start_time ? formatTime(match.start_time) : '');

const getMarketCode = (market) => String(market?.market_code || market?.market_type?.code || '').toUpperCase();

const getMarketSelections = (market) => Array.isArray(market?.odds || market?.selections)
  ? (market.odds || market.selections)
  : [];
const getLineGroupSelections = (lineGroup) => Array.isArray(lineGroup?.selections || lineGroup?.odds)
  ? (lineGroup.selections || lineGroup.odds)
  : [];
const getLineGroupDisplay = (lineGroup) => (
  lineGroup?.display
  ?? lineGroup?.lineDisplay
  ?? lineGroup?.line_group_display
  ?? lineGroup?.lineValue
  ?? lineGroup?.signedLine
  ?? lineGroup?.signedBaseLine
  ?? ''
).toString().trim();
const getMarketLineGroups = (market) => {
  const v2LineGroups = Array.isArray(market?.lineGroups)
    ? market.lineGroups.map((lineGroup, index) => ({
      ...lineGroup,
      key: getInplayLineGroupStableKey(lineGroup, market) || lineGroup.key || lineGroup.lineId || lineGroup.line_id || `line-${index}`,
      selections: getLineGroupSelections(lineGroup),
    })).filter((lineGroup) => lineGroup.selections.length > 0)
    : [];
  if (v2LineGroups.length > 0) return v2LineGroups;
  const selections = Array.isArray(market?._mergedOdds) ? market._mergedOdds : getMarketSelections(market);
  return selections.length > 0
    ? [{
      key: market?._groupKey || market?.id || market?.market_id || 'default-line',
      display: market?._baseLineLabel || market?.display_line || market?.base_line || market?.line || '',
      selections,
    }]
    : [];
};
const withLineGroupMarketContext = (market, lineGroup) => {
  const lineDisplay = getLineGroupDisplay(lineGroup);
  return {
    ...market,
    lineGroups: [lineGroup],
    _lineGroupKey: getInplayLineGroupStableKey(lineGroup, market) || lineGroup?.key || lineGroup?.lineId || null,
    _lineGroupDisplay: lineDisplay || null,
    _baseLineLabel: lineDisplay || market?._baseLineLabel || null,
    display_line: lineDisplay || market?.display_line,
    base_line: lineDisplay || market?.base_line,
    line: lineDisplay || market?.line,
  };
};
const getMarketSelectionCount = (markets = []) => (
  markets.reduce((sum, market) => sum + getMarketLineGroups(market).reduce((lineSum, lineGroup) => (
    lineSum + getLineGroupSelections(lineGroup).length
  ), 0), 0)
);
const hasMarketSelections = (markets = []) => getMarketSelectionCount(markets) > 0;
const filterMarketsWithSelections = (markets = []) => (
  markets.filter((market) => getMarketLineGroups(market).length > 0)
);

const normalizeLiveToken = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const matchesLiveTeamName = (candidate, ...teamNames) => {
  const normalized = normalizeLiveToken(candidate);
  if (!normalized) return false;
  return teamNames.filter(Boolean).some((teamName) => normalizeLiveToken(teamName) === normalized);
};

const getSelectionOutcomeToken = (selection, match) => {
  const rawName = String(selection?.name || '').trim();
  const rawNameKo = String(selection?.name_ko || selection?.selection_display_label || '').trim();
  const normalized = normalizeLiveToken(rawName);
  const normalizedKo = normalizeLiveToken(rawNameKo);

  if (
    ['1', 'home', 'home win', 'home team', '홈', '홈승'].includes(normalized)
    || ['홈', '홈승'].includes(normalizedKo)
    || matchesLiveTeamName(rawName, match?.home_team, match?.home_team_ko)
    || matchesLiveTeamName(rawNameKo, match?.home_team, match?.home_team_ko)
  ) return '1';

  if (
    ['2', 'away', 'away win', 'away team', '원정', '원정승'].includes(normalized)
    || ['원정', '원정승'].includes(normalizedKo)
    || matchesLiveTeamName(rawName, match?.away_team, match?.away_team_ko)
    || matchesLiveTeamName(rawNameKo, match?.away_team, match?.away_team_ko)
  ) return '2';

  if (
    ['x', 'draw', 'tie', '무', '무승부'].includes(normalized)
    || ['무', '무승부'].includes(normalizedKo)
  ) return 'X';

  return rawName || rawNameKo || '';
};

const getSelectionTokens = (market, match) => new Set(
  getMarketSelections(market)
    .map((selection) => getSelectionOutcomeToken(selection, match))
    .filter(Boolean),
);

const findOutcomeSelection = (odds, match, token) => (
  odds.find((selection) => getSelectionOutcomeToken(selection, match) === token) || null
);

const isBasketballLiveMatch = (match) => {
  if (Number(match?.sport_id) === 2) return true;
  const sport = match?.sport || {};
  const tokens = [
    sport.slug,
    sport.name,
    sport.name_ko,
    match?.sport_slug,
    match?.sport_name,
  ].map(normalizeLiveToken).filter(Boolean);
  return tokens.some((token) => token === 'basketball' || token === '농구');
};

const getV2ProductGroup = (market) => (
  market?.productGroup
  || market?.v2_display_model?.productGroup
  || ''
);

const isFulltimeV2Market = (market) => {
  const period = normalizeLiveToken(market?.periodLabel || market?.v2_display_model?.periodLabel || '');
  return !period || ['fulltime', 'full time', '풀타임', 'ft'].includes(period);
};

const getV2SelectionSide = (selection) => normalizeLiveToken(
  selection?.v2_display_model?.side
  || selection?.side
  || ''
);

const normalizeLiveMainMarketTitle = (market) => {
  const rawTitle = String(
    buildMarketDisplayTitle(market)
      || market?.market_display_label
      || market?.market_meaning_label
      || market?.market_name
      || market?.market_type_name
      || market?.market_type?.name
      || ''
  ).trim();

  if (!rawTitle) return '';

  return rawTitle
    .replace(/^풀타임\s*\/\s*/i, '')
    .replace(/^full\s*time\s*\/\s*/i, '')
    .replace(/\[?\s*연장포함\s*\]?/gi, '')
    .replace(/\(?\s*including\s+overtime\s*\)?/gi, '')
    .replace(/\(?\s*after\s+overtime\s*\)?/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

const isWholeMatchPrimaryTitle = (market) => {
  const normalizedTitle = normalizeLiveMainMarketTitle(market);
  return [
    '승패',
    '승무패',
    'winner',
    'money line',
    'match result',
    '1x2',
    '12',
  ].includes(normalizedTitle);
};

const isPrimaryLiveCardMarket = (market, match) => {
  if (!market || !isWholeMatchPrimaryTitle(market)) return false;
  const code = getMarketCode(market);
  const names = getSelectionTokens(market, match);
  if (!names.size) return false;
  if (isBasketballLiveMatch(match)) {
    if (code === 'MATCH_RESULT' || names.has('X')) return false;
    const productGroup = getV2ProductGroup(market);
    const v2Selections = getMarketSelections(market);
    const hasV2Shape = Boolean(market?.v2_display_model || Array.isArray(market?.lineGroups));
    if (hasV2Shape) {
      const sides = new Set(v2Selections.map(getV2SelectionSide).filter(Boolean));
      return productGroup === 'winner'
        && isFulltimeV2Market(market)
        && sides.has('home')
        && sides.has('away')
        && !sides.has('draw');
    }
    return (code === 'WINNER' || code === 'MONEY_LINE') && names.has('1') && names.has('2');
  }
  if (code === 'MATCH_RESULT') return names.has('1') && names.has('X') && names.has('2');
  if (code === 'WINNER' || code === 'MONEY_LINE') return names.has('1') && names.has('2');
  return false;
};

const isLockedFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

const isLiveMatchLockedForCard = (match) => (
  isLockedFlag(match?.betting_locked) ||
  isLockedFlag(match?.non_tradable) ||
  String(match?.betting_status || '').toLowerCase() === 'locked'
);

const findPrimaryLiveCardMarket = (markets, match) => (
  (Array.isArray(markets) ? markets : []).find((market) => isPrimaryLiveCardMarket(market, match)) || null
);

const findFirstSelectionMarket = (markets) => (
  (Array.isArray(markets) ? markets : []).find((market) => getMarketSelections(market).length > 0) || null
);

const resolvePrimaryLiveCardMarket = (match, marketSource = null, options = {}) => {
  const markets = Array.isArray(marketSource)
    ? marketSource
    : (Array.isArray(match?.markets) ? match.markets : []);
  const explicitMain = isPrimaryLiveCardMarket(match?.main_market, match) ? match.main_market : null;
  const refreshedMain = options.preferMarketSource ? findPrimaryLiveCardMarket(markets, match) : null;
  if (isBasketballLiveMatch(match)) {
    return refreshedMain || explicitMain || findPrimaryLiveCardMarket(markets, match) || null;
  }
  return refreshedMain || explicitMain || findPrimaryLiveCardMarket(markets, match) || findFirstSelectionMarket(markets);
};

const getAdditionalMarketCount = (match, marketSource = null, options = {}) => {
  const summaryCount = Number(match?.additional_market_count);
  if (Number.isFinite(summaryCount) && summaryCount >= 0) return summaryCount;
  const markets = Array.isArray(marketSource)
    ? marketSource
    : (Array.isArray(match?.markets) ? match.markets : []);
  if (!markets.length) return 0;
  const main = resolvePrimaryLiveCardMarket(match, markets, options);
  const mainKey = main ? getCustomerMarketKey(main) : null;
  const seen = new Set();
  return markets.reduce((count, mk) => {
    const key = getCustomerMarketKey(mk) || mk?.raw_market_id || `${mk?.market_code || mk?.market_type?.code || 'market'}:${mk?.line || mk?.display_line || mk?.base_line || ''}`;
    if (key === mainKey || seen.has(key)) return count;
    seen.add(key);
    return count + 1;
  }, 0);
};

const abbreviateTeamName = (name) => {
  if (!name) return '-';
  if (name.length <= 16) return name;
  return `${name.slice(0, 15)}…`;
};

const LIVE_SYNC_INTERVAL_MS = 60000;
const LIVE_CARD_MARKETS_INTERVAL_MS = 60000;
const LIVE_CARD_MARKETS_CACHE_TTL_MS = 55000;
const LIVE_CARD_MARKETS_RETAIN_TTL_MS = 60000;
const LIVE_CARD_MARKETS_RETAIN_MAX = 12;
const LIVE_CARD_MARKET_PREFETCH_LIMIT = 2;
const LIVE_ODDS_RECOVERY_MIN_INTERVAL_MS = 60000;
const LIVE_DETAIL_SHELL_SYNC_INTERVAL_MS = 4000;
const LIVE_CLOCK_TICK_MS = 5000;
const LIVE_DETAIL_INITIAL_MARKET_RENDER_LIMIT = 18;
const LIVE_DETAIL_MARKET_RENDER_CHUNK = 18;
const liveNoCacheParam = () => ({ _live_ts: Date.now() });

const SportsLive = ({
  mobileCartOpen = false,
  onToggleMobileCart = () => {},
  cartCount = 0,
}) => {
  const {
    matches, loading, pageLoading, oddsFlash, oddsLatest, marketLatest, liveMarketProjectionByMatchId, liveMatchStateByMatchId, selectedSport, displaySettings,
    refreshMatches, goToPage,
    currentPage, livePendingPage, totalPages, hasMoreMatches, hasPrevPage, matchTotal,
    registerDetailMatch, unregisterDetailMatch, primeOddsLatest, syncMatchDetailIntoList, clearInplayMatchMarkets, mergeInplaySnapshot,
    socketConnected, getLastWsEventAt, lastSocketConnectedAt, socketRecoveryNonce, lastSubscribeAckAt,
    liveCartMatchIds, terminalLiveMatchIds,
  } = useContext(SportsContext);
  const _fmt = displaySettings?.default_odds_format || 'decimal';
  const navigate = useNavigate();
  const { toggleSelection, isSelected } = useContext(SportsBetCartContext);
  const { services: { SportsService } } = useServiceContextHook();

  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [matchDetail, setMatchDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [liveClickFeedbackPage, setLiveClickFeedbackPage] = useState(null);
  const [liveCardMarketsByMatchId, setLiveCardMarketsByMatchId] = useState({});
  const [visibleDetailMarketCount, setVisibleDetailMarketCount] = useState(LIVE_DETAIL_INITIAL_MARKET_RENDER_LIMIT);
  const [mobileView, setMobileView] = useState('list');
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= 960 : false
  ));
  const [clockTick, setClockTick] = useState(() => Date.now());
  const detailSeqRef = useRef(0);
  const liveCardMarketsCacheRef = useRef(new Map());
  const liveCardMarketsInFlightRef = useRef(new Map());
  const liveCardMarketsNotFoundRef = useRef(new Set());
  const lastLiveOddsRecoveryAtRef = useRef(new Map());
  const lastSilentRecoveryAtRef = useRef(0);
  const lastListSilentRecoveryAtRef = useRef(0);
  const liveClickFeedbackTimerRef = useRef(null);
  const livePageNavigationFrameRef = useRef(null);
  const recoveryStateRef = useRef({
    socketConnected: false,
    lastSocketConnectedAt: 0,
    lastSubscribeAckAt: 0,
  });

  useEffect(() => {
    recoveryStateRef.current = {
      socketConnected,
      lastSocketConnectedAt,
      lastSubscribeAckAt,
    };
  }, [lastSocketConnectedAt, lastSubscribeAckAt, socketConnected]);

  const getRecoveryState = useCallback(() => ({
    ...recoveryStateRef.current,
    lastWsEventAt: Number(getLastWsEventAt?.() || 0),
  }), [getLastWsEventAt]);

  const clearLiveClickFeedbackTimer = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (liveClickFeedbackTimerRef.current) {
      window.clearTimeout(liveClickFeedbackTimerRef.current);
      liveClickFeedbackTimerRef.current = null;
    }
  }, []);

  const markLivePageFeedback = useCallback((page) => {
    const normalizedPage = Number(page);
    if (!Number.isFinite(normalizedPage) || normalizedPage < 1) return;
    clearLiveClickFeedbackTimer();
    flushSync(() => {
      setLiveClickFeedbackPage(normalizedPage);
    });
    if (typeof window !== 'undefined') {
      liveClickFeedbackTimerRef.current = window.setTimeout(() => {
        setLiveClickFeedbackPage((current) => (
          Number(current) === normalizedPage ? null : current
        ));
        liveClickFeedbackTimerRef.current = null;
      }, 1800);
    }
  }, [clearLiveClickFeedbackTimer]);

  const scheduleLivePageNavigation = useCallback((callback) => {
    if (typeof window === 'undefined') {
      callback();
      return;
    }
    if (livePageNavigationFrameRef.current) {
      window.cancelAnimationFrame(livePageNavigationFrameRef.current);
    }
    livePageNavigationFrameRef.current = window.requestAnimationFrame(() => {
      livePageNavigationFrameRef.current = null;
      callback();
    });
  }, []);

  useEffect(() => {
    if (!liveClickFeedbackPage) return undefined;
    if (Number(currentPage || 1) !== Number(liveClickFeedbackPage)) return undefined;
    const timer = window.setTimeout(() => {
      setLiveClickFeedbackPage((current) => (
        Number(current) === Number(liveClickFeedbackPage) ? null : current
      ));
    }, 140);
    return () => window.clearTimeout(timer);
  }, [currentPage, liveClickFeedbackPage]);

  useEffect(() => () => {
    clearLiveClickFeedbackTimer();
    if (typeof window !== 'undefined' && livePageNavigationFrameRef.current) {
      window.cancelAnimationFrame(livePageNavigationFrameRef.current);
      livePageNavigationFrameRef.current = null;
    }
  }, [clearLiveClickFeedbackTimer]);

  const rememberLiveCardMarkets = useCallback((matchId, markets, options = {}) => {
    const normalizedMatchId = Number(matchId);
    if (!normalizedMatchId || !Array.isArray(markets) || markets.length === 0) return false;
    const displayableMarkets = filterMarketsWithSelections(markets);
    if (displayableMarkets.length === 0) return false;

    liveCardMarketsCacheRef.current.set(normalizedMatchId, {
      fetchedAt: options.fetchedAt ?? Date.now(),
      markets: displayableMarkets,
    });
    return true;
  }, []);

  const syncRetainedLiveCardMarkets = useCallback((activeMatchIds = []) => {
    const now = Date.now();
    const activeSet = new Set(
      activeMatchIds
        .map((value) => Number(value))
        .filter(Boolean)
    );

    const retainedEntries = Array.from(liveCardMarketsCacheRef.current.entries())
      .filter(([matchId, entry]) => {
        if (!Array.isArray(entry?.markets)) return false;
        if (activeSet.has(matchId)) return true;
        if (entry.markets.length === 0) return false;
        return now - entry.fetchedAt < LIVE_CARD_MARKETS_RETAIN_TTL_MS;
      })
      .sort((left, right) => {
        const leftActive = activeSet.has(left[0]) ? 1 : 0;
        const rightActive = activeSet.has(right[0]) ? 1 : 0;
        if (leftActive !== rightActive) return rightActive - leftActive;
        return (right[1]?.fetchedAt || 0) - (left[1]?.fetchedAt || 0);
      })
      .slice(0, LIVE_CARD_MARKETS_RETAIN_MAX);

    const keepIds = new Set(retainedEntries.map(([matchId]) => matchId));
    Array.from(liveCardMarketsCacheRef.current.keys()).forEach((matchId) => {
      if (!keepIds.has(matchId)) liveCardMarketsCacheRef.current.delete(matchId);
    });

    setLiveCardMarketsByMatchId(
      retainedEntries.reduce((next, [matchId, entry]) => {
        next[matchId] = entry.markets;
        return next;
      }, {})
    );
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(max-width: 960px)');
    const sync = (event) => setIsMobileViewport(event.matches);
    setIsMobileViewport(media.matches);
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  const hasRunningClock = useMemo(() => (
    matches.some((match) => match?.live_minute !== null && match?.live_minute !== undefined && match?.live_minute !== '')
  ), [matches]);

  useEffect(() => {
    if (!hasRunningClock || typeof window === 'undefined') return undefined;
    const timer = window.setInterval(() => setClockTick(Date.now()), LIVE_CLOCK_TICK_MS);
    return () => window.clearInterval(timer);
  }, [hasRunningClock]);

  const selectedSportId = selectedSport?.id ? Number(selectedSport.id) : null;
  const isSelectedSport = useCallback((match) => {
    if (!selectedSportId) return true;
    const sportId = Number(match?.sport?.id || match?.sport_id || 0);
    return sportId === selectedSportId;
  }, [selectedSportId]);

  /** 좌측 레일 종목 선택을 LIVE 보드에도 그대로 연결한다. */
  const liveMatches = useMemo(() =>
    matches.filter((m) => (
      (m.is_live || m.status === 'LIVE')
      && m.is_visible !== false
      && m.visible !== false
      && m.user_visible !== false
      && m.isUserVisible !== false
      && isSelectedSport(m)
    )),
  [matches, isSelectedSport]);
  const upcomingMatches = useMemo(() =>
    matches.filter((m) => !m.is_live && m.status !== 'LIVE' && isSelectedSport(m)),
  [matches, isSelectedSport]);

  /** 경기 상세 로드 */
  const loadMatchDetail = useCallback(async (matchId, options = {}) => {
    const shellOnly = options.shellOnly === true;
    if (!matchId) {
      if (!shellOnly) {
        detailSeqRef.current += 1;
        setMatchDetail(null);
      }
      return;
    }
    const seq = shellOnly ? detailSeqRef.current : ++detailSeqRef.current;
    const silent = options.silent === true;
    if (!silent) setDetailLoading(true);
    try {
      const requestStartedAt = Date.now();
      const res = await SportsService.getMatchDetail(matchId, {
        betting_category: 'LIVE',
        ...(shellOnly ? { shell_only: 1 } : {}),
        ...liveNoCacheParam(),
      });
      if (!shellOnly && seq !== detailSeqRef.current) return;
      if (res?.status) {
        if (shellOnly) {
          syncMatchDetailIntoList?.(res.data, {
            requestStartedAt,
            source: 'rest_live_detail_shell_poll',
          });
          return;
        }
        setMatchDetail(prev => mergeInplaySnapshot?.(prev, res.data, { source: 'rest_live_detail' }) || res.data);
        syncMatchDetailIntoList?.(res.data, { requestStartedAt });
        primeOddsLatest?.(res.data?.markets, {
          overwrite: true,
          flashChanges: false,
          requestStartedAt,
          source: 'rest_live_detail',
        });
      } else if (!shellOnly && isNotFoundSportsResponse(res)) {
        clearInplayMatchMarkets?.(matchId, {
          source: 'rest_live_detail_not_found',
          hidden: true,
        });
        setMatchDetail(prev => (
          Number(prev?.id) === Number(matchId)
            ? { ...prev, markets: [], is_visible: false }
            : prev
        ));
      }
    } catch (e) {
      if (seq === detailSeqRef.current) console.error('상세 로드 실패:', e);
    } finally {
      if (!silent && !shellOnly && seq === detailSeqRef.current) setDetailLoading(false);
    }
  }, [SportsService, clearInplayMatchMarkets, mergeInplaySnapshot, primeOddsLatest, syncMatchDetailIntoList]);

  const shouldThrottleLiveOddsRecovery = useCallback((matchId, options = {}, now = Date.now()) => {
    const normalizedMatchId = Number(matchId);
    if (!normalizedMatchId) return true;
    const minIntervalMs = Number(options.minIntervalMs || LIVE_ODDS_RECOVERY_MIN_INTERVAL_MS);
    const lastRecoveredAt = Number(lastLiveOddsRecoveryAtRef.current.get(normalizedMatchId) || 0);
    return lastRecoveredAt > 0 && now - lastRecoveredAt < minIntervalMs;
  }, []);

  const markLiveOddsRecovery = useCallback((matchId, at = Date.now()) => {
    const normalizedMatchId = Number(matchId);
    if (!normalizedMatchId) return;
    lastLiveOddsRecoveryAtRef.current.set(normalizedMatchId, at);
  }, []);

  const fetchLiveCardMarkets = useCallback(async (matchId, options = {}) => {
    const normalizedMatchId = Number(matchId);
    if (!normalizedMatchId) return null;

    const now = Date.now();
    const cached = liveCardMarketsCacheRef.current.get(normalizedMatchId);
    const cacheFresh = cached && now - cached.fetchedAt < LIVE_CARD_MARKETS_CACHE_TTL_MS;
    if (!options.force && cacheFresh) {
      return cached.markets;
    }

    if (shouldThrottleLiveOddsRecovery(normalizedMatchId, options, now)) {
      return cached?.markets || null;
    }

    const inflight = liveCardMarketsInFlightRef.current.get(normalizedMatchId);
    if (inflight) return inflight;

    markLiveOddsRecovery(normalizedMatchId, now);
    const request = SportsService.getMatchOdds(normalizedMatchId, {
      betting_category: 'LIVE',
      compact: 1,
      ...liveNoCacheParam(),
    })
      .then((response) => {
        if (isNotFoundSportsResponse(response)) {
          liveCardMarketsCacheRef.current.delete(normalizedMatchId);
          liveCardMarketsNotFoundRef.current.add(normalizedMatchId);
          return [];
        }
        liveCardMarketsNotFoundRef.current.delete(normalizedMatchId);
        if (response?.status && response?.data) {
          syncMatchDetailIntoList?.(
            { ...response.data, id: response.data?.id ?? normalizedMatchId },
            {
              requestStartedAt: now,
              source: 'rest_live_odds',
            },
          );
        }
        const markets = Array.isArray(response?.data?.markets) ? response.data.markets : null;
        if (Array.isArray(markets)) {
          rememberLiveCardMarkets(normalizedMatchId, markets);
        }
        return markets;
      })
      .catch(() => null)
      .finally(() => {
        liveCardMarketsInFlightRef.current.delete(normalizedMatchId);
      });

    liveCardMarketsInFlightRef.current.set(normalizedMatchId, request);
    return request;
  }, [
    SportsService,
    markLiveOddsRecovery,
    rememberLiveCardMarkets,
    shouldThrottleLiveOddsRecovery,
    syncMatchDetailIntoList,
  ]);

  /** 경기 선택 */
  const handleMatchSelect = useCallback((matchId) => {
    if (Number(selectedMatchId) === Number(matchId)) {
      if (isMobileViewport) setMobileView('detail');
      return;
    }
    setSelectedMatchId(matchId);
    setCollapsedGroups({});
    if (isMobileViewport) setMobileView('detail');
  }, [isMobileViewport, selectedMatchId]);

  const detailTargetId = useMemo(() => resolveStickyLiveDetailTargetId({
    selectedMatchId,
    liveMatches,
  }), [liveMatches, selectedMatchId]);

  const liveMarketProjectionEntry = useMemo(() => {
    const key = detailTargetId ? String(detailTargetId) : '';
    return key ? liveMarketProjectionByMatchId?.[key] || null : null;
  }, [detailTargetId, liveMarketProjectionByMatchId]);

  const liveMatchStateEntry = useMemo(() => {
    const key = detailTargetId ? String(detailTargetId) : '';
    return key ? liveMatchStateByMatchId?.[key] || null : null;
  }, [detailTargetId, liveMatchStateByMatchId]);

  useEffect(() => {
    const selectedId = Number(selectedMatchId);
    if (!selectedId) return;
    const selectedRow = matches.find((match) => Number(match?.id) === selectedId);
    const terminalByWs = (terminalLiveMatchIds || []).some((matchId) => Number(matchId) === selectedId);
    if (!terminalByWs && (!selectedRow || !isTerminalLiveMatchStatus(selectedRow))) return;
    setSelectedMatchId(null);
  }, [matches, selectedMatchId, terminalLiveMatchIds]);

  const liveCardMatchIds = useMemo(() => (
    buildStickyLiveCardMatchIds({
      liveMatches,
      detailTargetId,
      cartMatchIds: liveCartMatchIds,
      prefetchLimit: LIVE_CARD_MARKET_PREFETCH_LIMIT,
    })
  ), [detailTargetId, liveCartMatchIds, liveMatches]);

  const liveCardMatchKey = useMemo(() => (
    liveCardMatchIds.join(',')
  ), [liveCardMatchIds]);

  const refreshDetailMarkets = useCallback(async (matchId, options = {}) => {
    const normalizedMatchId = Number(matchId);
    if (!normalizedMatchId) return;

    const requestStartedAt = Date.now();
    const markets = await fetchLiveCardMarkets(normalizedMatchId, {
      force: options.force === true,
      minIntervalMs: LIVE_ODDS_RECOVERY_MIN_INTERVAL_MS,
    });
    if (!Array.isArray(markets)) return;

    if (markets.length === 0) {
      const hiddenByNotFound = liveCardMarketsNotFoundRef.current.has(normalizedMatchId);
      liveCardMarketsNotFoundRef.current.delete(normalizedMatchId);
      liveCardMarketsCacheRef.current.delete(normalizedMatchId);
      setLiveCardMarketsByMatchId(prev => {
        if (!prev?.[normalizedMatchId]) return prev;
        const next = { ...prev };
        delete next[normalizedMatchId];
        return next;
      });
      if (Number(detailTargetId) === normalizedMatchId) {
        setMatchDetail(prev => mergeInplaySnapshot?.(
          prev,
          { id: normalizedMatchId, markets: [] },
          {
            source: 'rest_live_detail_refresh',
            replaceMarkets: true,
            replaceSelections: true,
          }
        ) || prev);
      }
      syncMatchDetailIntoList?.({ id: normalizedMatchId }, {
        markets: [],
        requestStartedAt,
        clearStale: true,
        hidden: hiddenByNotFound || options.hidden === true,
        source: hiddenByNotFound || options.hidden === true ? 'rest_live_odds_not_found' : 'rest_live_detail_empty',
      });
      return;
    }

    if (rememberLiveCardMarkets(normalizedMatchId, markets)) {
      syncRetainedLiveCardMarkets(liveCardMatchIds);
    }
    if (Number(detailTargetId) === normalizedMatchId) {
      setMatchDetail(prev => mergeInplaySnapshot?.(
        prev,
        { id: normalizedMatchId, markets },
        {
          source: 'rest_live_detail_refresh',
          replaceMarkets: true,
          replaceSelections: true,
        }
      ) || prev);
    }
    syncMatchDetailIntoList?.({ id: normalizedMatchId }, { markets, requestStartedAt });
    primeOddsLatest?.(markets, {
      overwrite: true,
      flashChanges: false,
      requestStartedAt,
      source: 'rest_live_detail_refresh',
    });
  }, [
    fetchLiveCardMarkets,
    detailTargetId,
    liveCardMatchIds,
    mergeInplaySnapshot,
    primeOddsLatest,
    rememberLiveCardMarkets,
    syncRetainedLiveCardMarkets,
    syncMatchDetailIntoList,
  ]);

  useEffect(() => {
    if (detailTargetId) loadMatchDetail(detailTargetId);
    else setMatchDetail(null);
  }, [detailTargetId, loadMatchDetail]);

  useEffect(() => {
    const matchId = Number(detailTargetId);
    if (!matchId || typeof window === 'undefined') return undefined;
    let cancelled = false;
    let syncInFlight = false;
    const syncShell = async () => {
      if (cancelled || syncInFlight) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      syncInFlight = true;
      try {
        await loadMatchDetail(matchId, { silent: true, shellOnly: true });
      } finally {
        syncInFlight = false;
      }
    };

    const interval = window.setInterval(syncShell, LIVE_DETAIL_SHELL_SYNC_INTERVAL_MS);
    syncShell();
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [detailTargetId, loadMatchDetail]);

  useEffect(() => {
    if (!matchDetail?.id || !Array.isArray(matchDetail?.markets) || matchDetail.markets.length === 0) return;
    if (!rememberLiveCardMarkets(matchDetail.id, matchDetail.markets)) return;
    syncRetainedLiveCardMarkets(liveCardMatchIds);
  }, [liveCardMatchIds, matchDetail, rememberLiveCardMarkets, syncRetainedLiveCardMarkets]);

  useEffect(() => {
    const clearedIds = Object.entries(liveMarketProjectionByMatchId || {})
      .filter(([, entry]) => Array.isArray(entry?.markets) && entry.markets.length === 0)
      .map(([matchId]) => Number(matchId))
      .filter(Boolean);
    if (clearedIds.length === 0) return;

    clearedIds.forEach((matchId) => liveCardMarketsCacheRef.current.delete(matchId));
    setLiveCardMarketsByMatchId(prev => {
      let changed = false;
      const next = { ...prev };
      clearedIds.forEach((matchId) => {
        if (next[matchId]) {
          delete next[matchId];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [liveMarketProjectionByMatchId]);

  useEffect(() => {
    const activeMatchIds = liveCardMatchKey
      ? liveCardMatchKey.split(',').map((value) => Number(value)).filter(Boolean)
      : [];

    if (shouldClearLiveCardMarketCache({
      activeMatchIds,
      detailTargetId,
      cartMatchIds: liveCartMatchIds,
    })) {
      liveCardMarketsCacheRef.current.clear();
      setLiveCardMarketsByMatchId({});
      return undefined;
    }

    let cancelled = false;
    let syncInFlight = false;

    const shouldRunCardRecovery = () => {
      const recoveryState = getRecoveryState();
      return shouldRunInplaySilentRecovery({
        socketConnected: recoveryState.socketConnected,
        lastWsEventAt: recoveryState.lastWsEventAt,
        lastSocketConnectedAt: Math.max(
          Number(recoveryState.lastSocketConnectedAt || 0),
          Number(recoveryState.lastSubscribeAckAt || 0)
        ),
        lastRecoveryAt: lastSilentRecoveryAtRef.current,
        gapMs: Number.POSITIVE_INFINITY,
        minIntervalMs: LIVE_ODDS_RECOVERY_MIN_INTERVAL_MS,
      });
    };

    const syncLiveCardMarkets = async (options = {}) => {
      if (cancelled || syncInFlight) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (!options.force && !shouldRunCardRecovery()) return;
      syncInFlight = true;

      const results = await Promise.all(activeMatchIds.map(async (matchId) => [
        matchId,
        await fetchLiveCardMarkets(matchId, {
          force: options.force === true,
          minIntervalMs: LIVE_ODDS_RECOVERY_MIN_INTERVAL_MS,
        }),
      ]));

      try {
        if (cancelled) return;
        lastSilentRecoveryAtRef.current = Date.now();

        const primedMarkets = [];
        results.forEach(([matchId, markets]) => {
          if (rememberLiveCardMarkets(matchId, markets)) {
            primedMarkets.push(...markets);
          }
        });
        syncRetainedLiveCardMarkets(activeMatchIds);

        if (primedMarkets.length > 0) {
          primeOddsLatest?.(primedMarkets, { overwrite: false });
        }
      } finally {
        syncInFlight = false;
      }
    };

    syncLiveCardMarkets();
    const interval = window.setInterval(syncLiveCardMarkets, LIVE_CARD_MARKETS_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    fetchLiveCardMarkets,
    getRecoveryState,
    detailTargetId,
    liveCartMatchIds,
    liveCardMatchKey,
    primeOddsLatest,
    rememberLiveCardMarkets,
    syncRetainedLiveCardMarkets,
  ]);

  useEffect(() => {
    const matchId = Number(detailTargetId);
    if (!matchId) return undefined;
    registerDetailMatch(matchId);
    return () => unregisterDetailMatch(matchId);
  }, [detailTargetId, registerDetailMatch, unregisterDetailMatch]);

  useEffect(() => {
    if (liveMatches.length > 0 && !lastListSilentRecoveryAtRef.current) {
      lastListSilentRecoveryAtRef.current = Date.now();
    }
  }, [liveMatches.length]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    let syncInFlight = false;
    const syncLive = async () => {
      if (pageLoading) return;
      if (syncInFlight || document.visibilityState !== 'visible') return;
      const recoveryState = getRecoveryState();
      if (!shouldRunInplaySilentRecovery({
        socketConnected: recoveryState.socketConnected,
        lastWsEventAt: recoveryState.lastWsEventAt,
        lastSocketConnectedAt: Math.max(
          Number(recoveryState.lastSocketConnectedAt || 0),
          Number(recoveryState.lastSubscribeAckAt || 0)
        ),
        lastRecoveryAt: lastListSilentRecoveryAtRef.current,
        gapMs: Number.POSITIVE_INFINITY,
        minIntervalMs: LIVE_SYNC_INTERVAL_MS,
      })) {
        return;
      }
      syncInFlight = true;
      try {
        lastListSilentRecoveryAtRef.current = Date.now();
        await refreshMatches?.(currentPage || 1, false, { forceLiveRequest: true });
      } finally {
        syncInFlight = false;
      }
    };
    const interval = window.setInterval(syncLive, LIVE_SYNC_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [
    currentPage,
    detailTargetId,
    getRecoveryState,
    pageLoading,
    refreshDetailMarkets,
    refreshMatches,
  ]);

  useEffect(() => {
    if (!socketRecoveryNonce || typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') return;
    const recoveryState = getRecoveryState();
    if (!shouldRunInplaySilentRecovery({
      socketConnected: recoveryState.socketConnected,
      lastWsEventAt: recoveryState.lastWsEventAt,
      lastSocketConnectedAt: Math.max(
        Number(recoveryState.lastSocketConnectedAt || 0),
        Number(recoveryState.lastSubscribeAckAt || 0)
      ),
      lastRecoveryAt: lastListSilentRecoveryAtRef.current,
      gapMs: LIVE_ODDS_RECOVERY_MIN_INTERVAL_MS,
      minIntervalMs: LIVE_SYNC_INTERVAL_MS,
    })) {
      return;
    }
    lastListSilentRecoveryAtRef.current = Date.now();
    Promise.all([
      refreshMatches?.(currentPage || 1, false, { forceLiveRequest: true }),
      detailTargetId ? refreshDetailMarkets(detailTargetId, { force: true }) : Promise.resolve(),
    ]).catch(() => {});
  }, [currentPage, detailTargetId, getRecoveryState, refreshDetailMarkets, refreshMatches, socketRecoveryNonce]);

  /** 선택된 경기 실시간 업데이트 반영 */
  const selectedMatch = useMemo(() => resolveStickyLiveSelectedShell({
    detailTargetId,
    matchDetail,
    matches,
    liveMatches,
  }), [detailTargetId, liveMatches, matchDetail, matches]);

  const activeMatch = useMemo(() => {
    const fallbackMatch = selectedMatch || null;
    if (!fallbackMatch) return null;
    const sameDetail = matchDetail && Number(matchDetail.id) === Number(fallbackMatch.id) ? matchDetail : null;
    return {
      id: fallbackMatch.id,
      home_team: sameDetail?.home_team || fallbackMatch.home_team,
      away_team: sameDetail?.away_team || fallbackMatch.away_team,
      home_team_ko: sameDetail?.home_team_ko || fallbackMatch.home_team_ko,
      away_team_ko: sameDetail?.away_team_ko || fallbackMatch.away_team_ko,
      home_team_logo_url: sameDetail?.home_team_logo_url || fallbackMatch.home_team_logo_url,
      away_team_logo_url: sameDetail?.away_team_logo_url || fallbackMatch.away_team_logo_url,
      // live fields: WS/live state first, REST detail shell fallback, stale list last.
      live_score_home: liveMatchStateEntry?.live_score_home ?? sameDetail?.live_score_home ?? fallbackMatch.live_score_home,
      live_score_away: liveMatchStateEntry?.live_score_away ?? sameDetail?.live_score_away ?? fallbackMatch.live_score_away,
      live_period: liveMatchStateEntry?.live_period ?? sameDetail?.live_period ?? fallbackMatch.live_period,
      live_minute: liveMatchStateEntry?.live_minute ?? sameDetail?.live_minute ?? fallbackMatch.live_minute,
      live_seconds: liveMatchStateEntry?.live_seconds ?? sameDetail?.live_seconds ?? fallbackMatch.live_seconds,
      live_clock_updated_at: liveMatchStateEntry?.live_clock_updated_at ?? sameDetail?.live_clock_updated_at ?? fallbackMatch.live_clock_updated_at,
      fixture_status: liveMatchStateEntry?.fixture_status ?? sameDetail?.fixture_status ?? fallbackMatch.fixture_status,
      fixture_status_label: liveMatchStateEntry?.fixture_status_label ?? sameDetail?.fixture_status_label ?? fallbackMatch.fixture_status_label,
      is_live: liveMatchStateEntry?.is_live ?? sameDetail?.is_live ?? fallbackMatch.is_live,
      status: liveMatchStateEntry?.status || sameDetail?.status || fallbackMatch.status,
      is_visible: liveMatchStateEntry?.is_visible ?? sameDetail?.is_visible ?? fallbackMatch.is_visible,
      visible: liveMatchStateEntry?.visible ?? sameDetail?.visible ?? fallbackMatch.visible,
      user_visible: liveMatchStateEntry?.user_visible ?? sameDetail?.user_visible ?? fallbackMatch.user_visible,
      isUserVisible: liveMatchStateEntry?.isUserVisible ?? sameDetail?.isUserVisible ?? fallbackMatch.isUserVisible,
      start_time: sameDetail?.start_time || fallbackMatch.start_time,
      league: sameDetail?.league || fallbackMatch.league,
      sport: sameDetail?.sport || fallbackMatch.sport,
      tracker: sameDetail?.tracker || fallbackMatch.tracker || false,
      tracker_data: sameDetail?.tracker_data || fallbackMatch.tracker_data || null,
      markets: Array.isArray(liveMarketProjectionEntry?.markets)
        ? liveMarketProjectionEntry.markets
        : (sameDetail?.markets || []),
    };
  }, [selectedMatch, matchDetail, liveMarketProjectionEntry, liveMatchStateEntry]);

  const detailMarkets = useMemo(() => {
    if (!detailTargetId) return [];
    const projectionMarkets = Array.isArray(liveMarketProjectionEntry?.markets)
      ? liveMarketProjectionEntry.markets
      : null;
    if (projectionMarkets) return filterMarketsWithSelections(projectionMarkets);
    if (Number(matchDetail?.id) !== Number(detailTargetId)) return [];
    const sourceMarkets = matchDetail?.markets;
    return Array.isArray(sourceMarkets) ? filterMarketsWithSelections(sourceMarkets) : [];
  }, [detailTargetId, matchDetail?.id, matchDetail?.markets, liveMarketProjectionEntry]);

  const normalizedDetailMarkets = useMemo(() => {
    if (!detailMarkets.length) return [];
    return _groupMarketsByBaseLine(detailMarkets);
  }, [detailMarkets]);

  useEffect(() => {
    const total = normalizedDetailMarkets.length;
    if (total === 0) {
      setVisibleDetailMarketCount(0);
      return undefined;
    }

    setVisibleDetailMarketCount(Math.min(total, LIVE_DETAIL_INITIAL_MARKET_RENDER_LIMIT));
    let cancelled = false;
    let timerId = null;

    const revealNextChunk = () => {
      if (cancelled) return;
      setVisibleDetailMarketCount((current) => {
        if (current >= total) return current;
        const next = Math.min(total, current + LIVE_DETAIL_MARKET_RENDER_CHUNK);
        if (next < total) {
          timerId = window.setTimeout(revealNextChunk, 40);
        }
        return next;
      });
    };

    if (total > LIVE_DETAIL_INITIAL_MARKET_RENDER_LIMIT && typeof window !== 'undefined') {
      timerId = window.setTimeout(revealNextChunk, 40);
    }

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [detailTargetId, normalizedDetailMarkets.length]);

  const visibleDetailMarkets = useMemo(() => (
    normalizedDetailMarkets.slice(0, visibleDetailMarketCount)
  ), [normalizedDetailMarkets, visibleDetailMarketCount]);

  const visualLivePage = Math.max(
    1,
    Math.floor(Number(liveClickFeedbackPage || livePendingPage || currentPage || 1) || 1),
  );
  const pageWindowStart = Math.max(1, Math.min(visualLivePage - 2, Math.max(totalPages - 4, 1)));
  const pageNumbers = Array.from(
    { length: Math.min(totalPages, 5) },
    (_, index) => pageWindowStart + index,
  ).filter((page) => page <= totalPages);

  const buildLivePageHref = useCallback((page) => {
    const params = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : '',
    );
    params.set('type', 'LIVE');
    params.set('page', String(page));
    params.set('live_page', String(page));
    if (selectedSportId) params.set('sport_id', String(selectedSportId));
    else params.delete('sport_id');
    return `/sports?${params.toString()}`;
  }, [selectedSportId]);

  const renderLivePageLink = useCallback((page, label, options = {}) => {
    const normalizedPage = Number(page);
    const disabled = options.disabled || !Number.isFinite(normalizedPage) || normalizedPage < 1;
    const active = Number(visualLivePage || 1) === normalizedPage;
    const pending = Number(livePendingPage || 0) === normalizedPage;
    const feedback = Number(liveClickFeedbackPage || 0) === normalizedPage;
    const className = `zxsb_pg_btn ${active ? 'zxsb_pg_active' : ''} ${pending ? 'zxsb_pg_pending' : ''} ${feedback ? 'zxsb_pg_feedback' : ''} ${disabled ? 'zxsb_pg_disabled' : ''}`;
    const href = buildLivePageHref(normalizedPage);

    if (disabled) {
      return (
        <span
          key={options.key || label}
          aria-disabled="true"
          className={className}
        >
          {label}
        </span>
      );
    }

    return (
      <Link
        key={options.key || label}
        to={href}
        aria-current={active ? 'page' : undefined}
        className={className}
        onPointerDown={(event) => {
          if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
            return;
          }
          markLivePageFeedback(normalizedPage);
        }}
        onKeyDown={(event) => {
          if (event.defaultPrevented || (event.key !== 'Enter' && event.key !== ' ')) return;
          markLivePageFeedback(normalizedPage);
        }}
        onClick={(event) => {
          if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
            return;
          }
          event.preventDefault();
          markLivePageFeedback(normalizedPage);
          scheduleLivePageNavigation(() => {
            const accepted = goToPage?.(normalizedPage, { deferLiveFetchToRoute: true });
            if (accepted !== false) navigate(href);
            else setLiveClickFeedbackPage(null);
          });
        }}
      >
        {label}
      </Link>
    );
  }, [buildLivePageHref, goToPage, liveClickFeedbackPage, livePendingPage, markLivePageFeedback, navigate, scheduleLivePageNavigation, visualLivePage]);

  const renderLivePager = useCallback(() => {
    if ((loading && liveMatches.length === 0) || matchTotal <= 0) return null;
    const showPagerLoading = pageLoading && !liveClickFeedbackPage;
    return (
      <div
        className={`zxsb_paging ${showPagerLoading ? 'zxsb_paging_loading' : ''}`}
        aria-busy={showPagerLoading}
      >
        {renderLivePageLink(visualLivePage - 1, '←', { key: 'prev', disabled: visualLivePage <= 1 })}
        {pageNumbers.map((page) => renderLivePageLink(page, page, { key: page }))}
        {renderLivePageLink(visualLivePage + 1, '→', { key: 'next', disabled: visualLivePage >= totalPages })}
        {showPagerLoading && <span className="zxsb_pg_loading_text">불러오는 중</span>}
      </div>
    );
  }, [liveClickFeedbackPage, liveMatches.length, loading, matchTotal, pageLoading, pageNumbers, renderLivePageLink, totalPages, visualLivePage]);

  /** 마켓 그룹 접기/펼치기 */
  const toggleCollapse = useCallback((groupKey) => {
    setCollapsedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  }, []);

  /** 배당 클릭 */
  const handleOddsClick = useCallback((match, market, oddsItem) => {
    // odds 가 merged section 에서 왔으면 source market_id 우선
    const resolvedMarketId = getSourceMarketId(oddsItem, market) ?? oddsItem.market_id ?? market.source_market_id ?? market.market_id ?? market.id;
    const marketDisplayKey = getCustomerMarketKey(oddsItem, market);
    // suspend pre-check: customer market 기준 (marketLatest 우선, fallback _sourceMarketSuspended)
    const _oddsSus = oddsLatest[oddsItem.id]?.is_suspended ?? oddsItem.is_suspended;
    const _srcMktSus = marketLatest[marketDisplayKey]?.is_suspended ?? oddsItem._sourceMarketSuspended ?? false;
    if (_oddsSus || _srcMktSus) return;
    const selectionNameRaw = oddsItem.name || oddsItem.name_ko || '';
    toggleSelection({
      match_id: match.id,
      market_id: resolvedMarketId,
      source_market_id: resolvedMarketId,
      customer_market_key: marketDisplayKey,
      market_group_key: `${match.id}:${marketDisplayKey}`,
      odds_id: oddsItem.id,
      inplay_odd_id: oddsItem.id,
      match_name: `${match.home_team_ko || match.home_team} vs ${match.away_team_ko || match.away_team}`,
      market_name: buildMarketDisplayTitle(market) || market.market_name || market.name || market.market_code || '마켓',
      market_title_display: buildMarketDisplayTitle(market),
      market_type_name: buildMarketDisplayTitle(market) || market.market_name || market.market_type_name || market.market_code || '',
      // 사용자 확정 규칙 (2026-04-08): HC 계열은 exact base_line, 그 외는 기존 fallback
      market_line: _isHCMarket(market)
        ? (market._baseLineLabel || market.base_line || '')
        : (market.display_line || market.base_line || market.line || market.home_line || ''),
      selection_name: selectionNameRaw,
      selection_name_display: getSelectionLabel(oddsItem, {
        market,
        match,
        fallback: oddsItem.name_ko || selectionNameRaw || '-',
      }),
      home_team: match.home_team,
      away_team: match.away_team,
      home_team_ko: match.home_team_ko,
      away_team_ko: match.away_team_ko,
      odds: oddsLatest[oddsItem.id]?.odds ?? oddsItem.odds,
    });
  }, [marketLatest, oddsLatest, toggleSelection]);

  /** 기본 마켓 추출 (카드용) — retained fresh card markets가 있을 때만 그 source를 우선한다. */
  const getMainOdds = (match, marketSource = null, options = {}) => {
    const main = resolvePrimaryLiveCardMarket(match, marketSource, options);
    return { market: main, odds: getMarketSelections(main) };
  };

  const renderCardOddsBtn = (odds, match, market, label, options = {}) => {
    const forceLocked = options.forceLocked === true || !market || isLiveMatchLockedForCard(match);
    const marketId = getCustomerMarketKey(odds || {}, market || {});
    const marketSuspended = (
      marketLatest?.[marketId]?.is_suspended ??
      market?.is_suspended ??
      market?.suspended ??
      market?.betting_locked ??
      market?.non_tradable ??
      false
    );
    const oddsSelfSus = odds
      ? (
        oddsLatest[odds.id]?.is_suspended ??
        odds.is_suspended ??
        odds.suspended ??
        odds.betting_locked ??
        odds.non_tradable ??
        false
      )
      : false;
    const srcMktSus = odds
      ? (marketLatest?.[getCustomerMarketKey(odds, market)]?.is_suspended ?? odds._sourceMarketSuspended ?? false)
      : false;
    const sus = Boolean(forceLocked || !odds || marketSuspended || oddsSelfSus || srcMktSus);
    const selectionStableKey = odds ? getInplaySelectionStableKey(odds, market) : null;
    const flash = odds ? (oddsFlash?.[odds.id] || (selectionStableKey ? oddsFlash?.[selectionStableKey] : null)) : null;
    const displayedOdds = odds ? formatOdds(oddsLatest[odds.id]?.odds ?? odds.odds, _fmt) : '잠김';
    return (
      <button
        type="button"
        className={`zi_odds_btn ${odds && isSelected(odds.id) ? 'zi_odds_sel' : ''} ${sus ? 'zi_odds_sus' : ''} ${flash ? 'zi_flash_' + flash.direction : ''}`}
        onClick={(e) => { e.stopPropagation(); !sus && handleOddsClick(match, market, odds); }}
        disabled={sus}
      >
        <span className="zi_odds_label">{label}</span>
        <span className={`zi_odds_val ${sus ? 'zi_odds_val_locked' : ''}`}>{displayedOdds}</span>
        {!sus && flash && <span className={`zi_delta zi_delta_${flash.direction}`}>{flash.direction === 'up' ? '+' : '-'}{formatDelta(flash.delta, _fmt)}</span>}
      </button>
    );
  };

  /** 모바일 뒤로가기 */
  const handleBack = () => {
    setMobileView('list');
  };

  return (
    <>
      <div className={`zi_board zi_live_board ${mobileView === 'detail' ? 'zi_live_mobile_detail' : ''}`}>
        <div className={`zi_list zi_live_list ${mobileView === 'detail' ? 'zi_live_list_hidden' : ''}`}>
          <div className="zi_list_header">
            <button type="button" className="zi_sort_btn zi_sort_active">실시간 경기 {liveMatches.length}</button>
            <button type="button" className="zi_sort_btn zi_sort_muted">곧 시작 {upcomingMatches.length}</button>
          </div>

          <div className="zi_list_scroll">
            {loading && liveMatches.length === 0 && !liveClickFeedbackPage && <SportsListSkeleton count={4} variant="international" />}
            {!loading && pageLoading && liveMatches.length === 0 && !liveClickFeedbackPage && <SportsListSkeleton count={4} variant="international" />}

            {liveMatches.map((m) => {
              const cachedCardMarkets = liveCardMarketsByMatchId[m.id];
              const cardMarkets = cachedCardMarkets || m.markets || [];
              const preferFreshCardMarkets = Array.isArray(cachedCardMarkets) && cachedCardMarkets.length > 0;
              const { market, odds } = getMainOdds(m, cardMarkets, {
                preferMarketSource: preferFreshCardMarkets,
              });
              const fallbackCardOdds = odds.slice(0, 3);
              const home = m.home_team_ko || m.home_team;
              const away = m.away_team_ko || m.away_team;
              const basketballCard = isBasketballLiveMatch(m);
              const homeOutcomeLabel = abbreviateTeamName(home);
              const awayOutcomeLabel = abbreviateTeamName(away);
              const homeOdds = findOutcomeSelection(odds, m, '1') || fallbackCardOdds[0] || null;
              const drawOdds = basketballCard ? null : findOutcomeSelection(odds, m, 'X');
              const awayOdds = findOutcomeSelection(odds, m, '2') || fallbackCardOdds[drawOdds ? 2 : 1] || null;
              const middleFallbackOdds = basketballCard
                ? null
                : (drawOdds || (!drawOdds && fallbackCardOdds.length >= 3 ? fallbackCardOdds[1] : null));
              const homeCardLabel = homeOutcomeLabel;
              const awayCardLabel = awayOutcomeLabel;
              const middleCardLabel = '무';
              const marketLocked = isLiveMatchLockedForCard(m)
                || !market
                || Boolean(
                  marketLatest?.[getCustomerMarketKey(market)]?.is_suspended ??
                  market?.is_suspended ??
                  market?.suspended ??
                  market?.betting_locked ??
                  market?.non_tradable ??
                  false
                );
              const flagUrl = getCountryFlagUrl(m.league);
              const isActive = detailTargetId === m.id;
              const startTimeLabel = formatTime(m.start_time);
              const scoreMetaLabel = getLiveHeaderTime(m, clockTick) || getLiveTimeDisplay(m);
              const additionalMarketCount = getAdditionalMarketCount(m, cardMarkets, {
                preferMarketSource: preferFreshCardMarkets,
              });
              const liveScore = getLiveScore(m);

              return (
                <div
                  key={m.id}
                  className={`zi_card ${isActive ? 'zi_card_active' : ''}`}
                  onClick={() => handleMatchSelect(m.id)}
                >
                  <div className="zi_card_head zi_live_card_head">
                    {flagUrl && <img src={flagUrl} alt="" className="zi_card_flag" />}
                    {renderSportIcon(m.sport)}
                    <span className="zi_card_league">{m.league?.name_ko || m.league?.name}</span>
                    <div className="zi_live_card_head_meta">
                      {startTimeLabel && <span className="zi_card_time">{startTimeLabel}</span>}
                      <span className="zi_live">LIVE</span>
                      <span className="zi_live_card_more">추가마켓 <strong>+{additionalMarketCount}</strong></span>
                    </div>
                  </div>

                  <div className="zi_card_body zi_live_card_body">
                    <div className="zi_live_card_matchup">
                      <div className="zi_card_team zi_live_card_team_side zi_live_card_team_side_home">
                        <TeamLogo url={m.home_team_logo_url} name={home} className="zi_card_logo" />
                        <span>{home}</span>
                      </div>
                      <div className="zi_live_card_score">
                        <div className="zi_live_card_score_row">
                          <strong>{liveScore.hasScore ? liveScore.home : '-'}</strong>
                          <span>:</span>
                          <strong>{liveScore.hasScore ? liveScore.away : '-'}</strong>
                        </div>
                        {scoreMetaLabel && (
                          <div className="zi_live_card_score_meta">{scoreMetaLabel}</div>
                        )}
                      </div>
                      <div className="zi_card_team zi_live_card_team_side zi_live_card_team_side_away">
                        <TeamLogo url={m.away_team_logo_url} name={away} className="zi_card_logo" />
                        <span>{away}</span>
                      </div>
                    </div>

                    <div className="zi_card_odds">
                      {renderCardOddsBtn(homeOdds, m, market, homeCardLabel, { forceLocked: marketLocked })}
                      {middleFallbackOdds ? (
                        renderCardOddsBtn(
                          middleFallbackOdds,
                          m,
                          market,
                          middleCardLabel,
                          { forceLocked: marketLocked }
                        )
                      ) : (
                        <div className="zi_odds_btn zi_live_mid_static">
                          <span className="zi_odds_label">VS</span>
                        </div>
                      )}
                      {renderCardOddsBtn(awayOdds, m, market, awayCardLabel, { forceLocked: marketLocked })}
                      <button
                        type="button"
                        className="zi_live_more_btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleMatchSelect(m.id);
                        }}
                        aria-label="라이브 상세 보기"
                      >
                        +{additionalMarketCount}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {!loading && !pageLoading && liveMatches.length === 0 && (
              <div className="zi_live_empty_panel">
                <strong>진행 중인 라이브 경기가 없습니다</strong>
              </div>
            )}

            {upcomingMatches.length > 0 && (
              <>
                <div className="zi_live_section_title">곧 시작</div>
                {upcomingMatches.slice(0, 8).map((m) => {
                  const flagUrl = getCountryFlagUrl(m.league);
                  return (
                    <div key={m.id} className="zi_card zi_card_soon">
                      <div className="zi_card_head">
                        {flagUrl && <img src={flagUrl} alt="" className="zi_card_flag" />}
                        {renderSportIcon(m.sport)}
                        <span className="zi_card_league">{m.league?.name_ko || m.league?.name}</span>
                        <span className="zi_live zi_live_soon">SOON</span>
                        <span className="zi_card_time">{formatTime(m.start_time)}</span>
                      </div>
                      <div className="zi_card_body zi_live_card_body zi_live_card_body_soon">
                        <div className="zi_card_teams">
                          <div className="zi_card_team">
                            <TeamLogo url={m.home_team_logo_url} name={m.home_team_ko || m.home_team} className="zi_card_logo" />
                            <span>{m.home_team_ko || m.home_team}</span>
                          </div>
                          <div className="zi_card_team">
                            <TeamLogo url={m.away_team_logo_url} name={m.away_team_ko || m.away_team} className="zi_card_logo" />
                            <span>{m.away_team_ko || m.away_team}</span>
                          </div>
                        </div>
                        <div className="zi_live_soon_hint">라이브 시작 대기</div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {renderLivePager()}
          </div>
        </div>

        {(!isMobileViewport || mobileView === 'detail') && (
        <div className="zi_detail zi_live_detail">
          {activeMatch ? (
            <>
              <div className="zi_detail_head">
                <button
                  type="button"
                  className="zi_live_back"
                  onClick={handleBack}
                  aria-label="경기 목록으로 돌아가기"
                >
                  ←
                </button>
                {(() => {
                  const flagUrl = getCountryFlagUrl(activeMatch.league);
                  return flagUrl ? <img src={flagUrl} alt="" className="zi_detail_flag" /> : null;
                })()}
                {renderSportIcon(activeMatch.sport)}
                <span className="zi_detail_league">{activeMatch.league?.name_ko || activeMatch.league?.name || ''}</span>
                {activeMatch.is_live ? (
                  <>
                    {activeMatch.start_time && <span className="zi_detail_time">{formatTime(activeMatch.start_time)}</span>}
                  </>
                ) : (
                  <span className="zi_detail_time">{formatTime(activeMatch.start_time)}</span>
                )}
              </div>

              <div className="zi_detail_hero zi_live_detail_hero">
                <div className="zi_detail_team zi_live_detail_team">
                  <TeamLogo url={activeMatch.home_team_logo_url} name={activeMatch.home_team_ko || activeMatch.home_team} className="zi_detail_logo" />
                  <span>{activeMatch.home_team_ko || activeMatch.home_team}</span>
                </div>
                <div className="zi_detail_score zi_live_detail_score zi_live_detail_scoreboard">
                  {(() => {
                    const liveScore = getLiveScore(activeMatch);
                    const liveHeader = getLiveHeaderTime(activeMatch, clockTick);
                    const livePhase = getLiveTimeDisplay(activeMatch);
                    return (
                      <>
                        <span className="zi_detail_vs">{liveScore.text}</span>
                        <span className="zi_live_detail_meta">{liveHeader || livePhase || '경기 시작 전'}</span>
                      </>
                    );
                  })()}
                </div>
                <div className="zi_detail_team zi_live_detail_team">
                  <TeamLogo url={activeMatch.away_team_logo_url} name={activeMatch.away_team_ko || activeMatch.away_team} className="zi_detail_logo" />
                  <span>{activeMatch.away_team_ko || activeMatch.away_team}</span>
                </div>
              </div>

              <div className="zi_detail_markets">
                {detailLoading && activeMatch.is_live && normalizedDetailMarkets.length === 0 && (
                  <div className="zxsb_loading">상세 로딩중...</div>
                )}

                {visibleDetailMarkets.map((mkt, index) => {
                  const lineGroups = getMarketLineGroups(mkt).map((lineGroup) => ({
                    ...lineGroup,
                    selections: _sortMergedOdds(
                      dedupeMergedSelections(getLineGroupSelections(lineGroup), oddsLatest, marketLatest)
                    ),
                  })).filter((lineGroup) => lineGroup.selections.length > 0);
                  const odds = lineGroups.flatMap((lineGroup) => lineGroup.selections);
                  const groupKey = mkt._groupKey || `${getCustomerMarketKey(mkt) || 'market'}::${mkt._baseLineLabel || index}`;
                  const isCollapsed = collapsedGroups[groupKey];
                  const mktSus = odds.length > 0 && odds.every(o => {
                    const oddsSelfSus = oddsLatest[o.id]?.is_suspended ?? o.is_suspended;
                    const srcSus = marketLatest[getCustomerMarketKey(o, mkt)]?.is_suspended ?? o._sourceMarketSuspended ?? false;
                    return oddsSelfSus || srcSus;
                  });
                  const titleMarket = lineGroups.length > 1
                    ? {
                      ...mkt,
                      _baseLineLabel: null,
                      display_line: null,
                      base_line: null,
                      line: null,
                    }
                    : mkt;

                  return (
                    <div key={groupKey} className={`zi_mkt_section ${mktSus ? 'zi_mkt_section_sus' : ''}`}>
                      <button type="button" className="zi_live_mkt_head" onClick={() => toggleCollapse(groupKey)}>
                        <span className="zi_mkt_title">{getMktTitleWithLine(titleMarket)}</span>
                        <span className="zi_live_mkt_toggle">{isCollapsed ? '▸' : '▾'}</span>
                      </button>

                      {!isCollapsed && (
                        <div className="zi_mkt_lines">
                          {lineGroups.map((lineGroup, lineIndex) => {
                            const lineMarket = withLineGroupMarketContext(mkt, lineGroup);
                            return (
                              <div key={getInplayLineGroupStableKey(lineGroup, mkt) || lineGroup.key || `${groupKey}::${lineIndex}`} className="zi_mkt_line_group">
                                <div className="zi_mkt_grid">
                                  {lineGroup.selections.map(o => {
                                    const oddsSelfSus = oddsLatest[o.id]?.is_suspended ?? o.is_suspended;
                                    const srcMktSus = marketLatest?.[getCustomerMarketKey(o, lineMarket)]?.is_suspended ?? o._sourceMarketSuspended ?? false;
                                    const sus = oddsSelfSus || srcMktSus;
                                    const selectionStableKey = getInplaySelectionStableKey(o, lineMarket);
                                    const flash = oddsFlash?.[o.id] || (selectionStableKey ? oddsFlash?.[selectionStableKey] : null);

                                    return (
                                      <button
                                        key={selectionStableKey || o.id}
                                        type="button"
                                        className={`zi_mkt_btn ${isSelected(o.id) ? 'zi_odds_sel' : ''} ${sus ? 'zi_odds_sus' : ''} ${flash ? 'zi_flash_' + flash.direction : ''}`}
                                        onClick={() => !sus && handleOddsClick(activeMatch, lineMarket, o)}
                                        disabled={sus}
                                      >
                                        <span className="zi_mkt_label">{getSelectionLabel(o, {
                                          market: lineMarket,
                                          match: activeMatch,
                                          fallback: '-',
                                        })}</span>
                                        <span className="zi_mkt_val">{formatOdds(oddsLatest[o.id]?.odds ?? o.odds, _fmt)}</span>
                                        {flash && <span className={`zi_delta zi_delta_${flash.direction}`}>{flash.direction === 'up' ? '+' : '-'}{formatDelta(flash.delta, _fmt)}</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {!detailLoading && normalizedDetailMarkets.length === 0 && (
                  <div className="zi_live_empty_panel">
                    <strong>선택 가능한 마켓이 없습니다</strong>
                  </div>
                )}
              </div>

              <button
                type="button"
                className={`zi_live_detail_cart_fab ${mobileCartOpen ? 'zi_live_detail_cart_fab_active' : ''}`}
                onClick={onToggleMobileCart}
              >
                <span className="zxsb_m_bottom_icon_wrap">
                  <ReceiptText size={16} strokeWidth={2.1} />
                  {cartCount > 0 && <span className="zxsb_m_bottom_badge">{cartCount}</span>}
                </span>
                <span>베팅카트</span>
              </button>
            </>
          ) : (
            <div className="zi_live_empty_panel zi_live_detail_empty">
              <strong>경기를 선택해 주세요</strong>
            </div>
          )}
        </div>
        )}
      </div>
    </>
  );
};

export default SportsLive;
